/**
 * EncounterFilters
 *
 * Filter dropdowns for status, type, recording/transcription status,
 * date range, and match status (connected mode).
 * Enterprise design with refined dropdowns and checkmark selections.
 */

import React, { useState } from 'react';
import { FunnelIcon, ChevronDownIcon, CalendarIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';
import {
  STATUS_OPTIONS,
  STANDALONE_STATUS_OPTIONS,
  TYPE_OPTIONS,
  RECORDING_STATUS_OPTIONS,
  TRANSCRIPTION_STATUS_OPTIONS,
  MATCH_STATUS_OPTIONS,
  DATE_PRESET_OPTIONS,
} from '../types';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, format, parseISO } from 'date-fns';

// ============================================================================
// Filter Dropdown Component
// ============================================================================

interface FilterDropdownProps {
  label: string;
  value: string[];
  options: { value: string; label: string }[];
  onChange: (values: string[]) => void;
  multiple?: boolean;
}

function FilterDropdown({ label, value, options, onChange, multiple = true }: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (optionValue: string) => {
    if (multiple) {
      const newValues = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange(newValues);
    } else {
      onChange(value.includes(optionValue) ? [] : [optionValue]);
      setIsOpen(false);
    }
  };

  const displayValue = value.length
    ? value.length === 1
      ? options.find((o) => o.value === value[0])?.label || value[0]
      : `${value.length} selected`
    : label;

  const hasSelection = value.length > 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-all ${
          hasSelection
            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
        }`}
      >
        <span className="truncate max-w-[120px]">{displayValue}</span>
        {hasSelection && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-blue-600 text-white rounded-full">
            {value.length}
          </span>
        )}
        <ChevronDownIcon
          className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 mt-1.5 w-56 bg-white border border-gray-200 rounded-xl shadow-clinical-medium py-1 max-h-80 overflow-auto">
            {options.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleToggle(option.value)}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 transition-colors ${
                    isSelected
                      ? 'bg-blue-50/60 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                  </span>
                  <span className="flex-1">{option.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Date Filter Component
// ============================================================================

interface DateFilterProps {
  dateFrom?: Date;
  dateTo?: Date;
  onChange: (from: Date | undefined, to: Date | undefined) => void;
  /**
   * Read the custom range's yyyy-MM-dd as local calendar days (standalone, AMP-141). Otherwise
   * new Date('yyyy-MM-dd') is UTC midnight, the previous day west of UTC; the connected list
   * keeps that behavior unchanged.
   */
  localDays?: boolean;
}

function DateFilter({ dateFrom, dateTo, onChange, localDays = false }: DateFilterProps) {
  const toInput = (d?: Date) => (d ? (localDays ? format(d, 'yyyy-MM-dd') : d.toISOString().split('T')[0]) : '');
  const fromInput = (v: string) => (v ? (localDays ? parseISO(v) : new Date(v)) : undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);

  const handlePreset = (preset: string) => {
    const now = new Date();
    let from: Date | undefined;
    let to: Date | undefined;

    switch (preset) {
      case 'today':
        from = startOfDay(now);
        to = endOfDay(now);
        break;
      case 'this-week':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'this-month':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'last-7-days':
        from = startOfDay(subDays(now, 7));
        to = endOfDay(now);
        break;
      case 'last-30-days':
        from = startOfDay(subDays(now, 30));
        to = endOfDay(now);
        break;
      case 'custom':
        setShowCustom(true);
        return;
      default:
        from = undefined;
        to = undefined;
    }

    onChange(from, to);
    setIsOpen(false);
    setShowCustom(false);
  };

  const hasDateFilter = dateFrom || dateTo;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-all ${
          hasDateFilter
            ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
        }`}
      >
        <CalendarIcon className="h-4 w-4" />
        <span>Date Range</span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
              setShowCustom(false);
            }}
          />
          <div className="absolute z-20 mt-1.5 w-64 bg-white border border-gray-200 rounded-xl shadow-clinical-medium py-1">
            {!showCustom ? (
              <>
                {DATE_PRESET_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handlePreset(option.value)}
                    className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {option.label}
                  </button>
                ))}
                {hasDateFilter && (
                  <button
                    type="button"
                    onClick={() => {
                      onChange(undefined, undefined);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 transition-colors"
                  >
                    Clear Date Filter
                  </button>
                )}
              </>
            ) : (
              <div className="p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    From
                  </label>
                  <input
                    type="date"
                    value={toInput(dateFrom)}
                    onChange={(e) => onChange(fromInput(e.target.value), dateTo)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    To
                  </label>
                  <input
                    type="date"
                    value={toInput(dateTo)}
                    onChange={(e) => onChange(dateFrom, fromInput(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowCustom(false)}
                    className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Filters Component
// ============================================================================

export function EncounterFilters() {
  const { config, filters, setFilters, isMobile } = useEncounterList();
  const [showAllFilters, setShowAllFilters] = useState(false);

  if (config.enableFilters === false) {
    return null;
  }

  const handleStatusChange = (values: string[]) => {
    setFilters({ status: values.length ? values : undefined });
  };

  const handleTypeChange = (values: string[]) => {
    setFilters({ type: values.length ? values : undefined });
  };

  const handleRecordingStatusChange = (values: string[]) => {
    setFilters({ recordingStatus: values.length ? values : undefined });
  };

  const handleTranscriptionStatusChange = (values: string[]) => {
    setFilters({ transcriptionStatus: values.length ? values : undefined });
  };

  const handleMatchStatusChange = (values: string[]) => {
    setFilters({ matchStatus: values.length ? values : undefined });
  };

  const handleDateChange = (from: Date | undefined, to: Date | undefined) => {
    setFilters({ dateFrom: from, dateTo: to });
  };

  const primaryFilters = (
    <>
      <FilterDropdown
        label="Status"
        value={filters.status || []}
        options={config.mode === 'standalone' ? STANDALONE_STATUS_OPTIONS : STATUS_OPTIONS}
        onChange={handleStatusChange}
      />
      <FilterDropdown
        label="Type"
        value={filters.type || []}
        options={TYPE_OPTIONS}
        onChange={handleTypeChange}
      />
      <DateFilter
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onChange={handleDateChange}
        localDays={config.mode === 'standalone'}
      />
    </>
  );

  const secondaryFilters = (
    <>
      <FilterDropdown
        label="Recording"
        value={filters.recordingStatus || []}
        options={RECORDING_STATUS_OPTIONS}
        onChange={handleRecordingStatusChange}
      />
      <FilterDropdown
        label="Transcription"
        value={filters.transcriptionStatus || []}
        options={TRANSCRIPTION_STATUS_OPTIONS}
        onChange={handleTranscriptionStatusChange}
      />
      {config.mode === 'connected' && (
        <FilterDropdown
          label="Match Status"
          value={filters.matchStatus || []}
          options={MATCH_STATUS_OPTIONS}
          onChange={handleMatchStatusChange}
        />
      )}
    </>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <FunnelIcon className="h-4 w-4" />
          <span className="font-medium hidden sm:inline">Filters</span>
        </div>

        {primaryFilters}

        {(!isMobile || showAllFilters) && secondaryFilters}

        {isMobile && (
          <button
            type="button"
            onClick={() => setShowAllFilters(!showAllFilters)}
            className="px-3 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:bg-blue-50 rounded-lg transition-colors"
          >
            {showAllFilters ? 'Less Filters' : 'More Filters'}
          </button>
        )}
      </div>
    </div>
  );
}

export default EncounterFilters;
